import { useEffect } from 'react';
import { Switch, Route, useHistory } from 'react-router-dom';
import { Box } from '@material-ui/core';

import { BackTo, CommonNavBarBlock } from '@vyce/core/src/components';
import { PageLoading } from '@vyce/core/src/components/PageLoading';

import { useFormData } from './hooks/useFormData';
import { useFormRouteSegment } from './hooks/useFormRouteSegment';
import { FormSummary } from './FormSummary';
import { useStyles } from './styles';
import { useFormAnswers } from './hooks/useFormAnswers';
import { FormResultByQuestion } from './FormResultByQuestion';
import { FormResultByUser } from './FormResultByUser';
import { FormRecipients } from './FormRecipients';
import { FormSettings } from './FormSettings';
import { Team, TimeAndAttendanceLocation, User } from '../../types';

export const FormAnswers: React.FC<{
  user: User;
  companyId: string;
  locations: TimeAndAttendanceLocation[];
  teams?: Team[];
  companyLogoUrl?: string | null;
}> = ({ user, companyId, locations, teams = [], companyLogoUrl }) => {
  const classes = useStyles();
  const history = useHistory();
  const { segment, id: formId } = useFormRouteSegment('id');
  const { form, loading, getForm } = useFormData({ userId: user.uuid, companyId });
  const {
    formSummary,
    loading: formSummaryLoading,
    getFormSummary,
  } = useFormAnswers({ userId: user.uuid, companyId });

  const goBack = () => {
    history.goBack();
  };

  useEffect(() => {
    if (formId) {
      getForm(formId);
      getFormSummary(formId);
    }
  }, [formId]);

  if (!formId) {
    return (
      <Box padding={2}>
        <BackTo backTo={{ text: 'Forms', back: goBack }} />
        Form not found
      </Box>
    );
  }

  if (loading || formSummaryLoading) {
    return <PageLoading />;
  }

  const tabBase = `/forms/form-answers/${segment}`;

  return (
    <div>
      <Box className="no-print">
        <BackTo backTo={{ text: 'Forms', back: goBack }} />
        <CommonNavBarBlock
          tabItems={[
            { label: 'Summary', link: `${tabBase}/summary` },
            { label: 'By question', link: `${tabBase}/by-question` },
            { label: 'By user', link: `${tabBase}/by-user` },
            { label: 'Recipients', link: `${tabBase}/recipients` },
            { label: 'Settings', link: `${tabBase}/settings` },
          ]}
        />
      </Box>

      <Box display="flex" flexDirection="column" className={classes.container}>
        <Switch>
          <Route
            path="/forms/form-answers/:id/summary"
            component={() => (
              <FormSummary
                form={form}
                formSummary={formSummary}
                user={user}
                companyId={companyId}
                companyLogoUrl={companyLogoUrl}
              />
            )}
          />
          <Route
            path="/forms/form-answers/:id/by-question"
            component={() => (
              <FormResultByQuestion
                form={form}
                formSummary={formSummary}
                user={user}
                companyLogoUrl={companyLogoUrl}
              />
            )}
          />
          <Route
            path="/forms/form-answers/:id/by-user"
            component={() => (
              <FormResultByUser
                form={form}
                user={user}
                companyId={companyId}
                companyLogoUrl={companyLogoUrl}
              />
            )}
          />
          <Route
            path="/forms/form-answers/:id/recipients"
            component={() => <FormRecipients form={form} user={user} companyId={companyId} />}
          />
          <Route
            path="/forms/form-answers/:id/settings"
            component={() => (
              <FormSettings
                form={form}
                user={user}
                companyId={companyId}
                locations={locations}
                teams={teams}
              />
            )}
          />
        </Switch>
      </Box>
    </div>
  );
};
